import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import Notifications from '../../../../../services/notifications';
import { compose } from '../../../../../services/utilityFunctions';
import translate from '../../../../../commonComponents/translate';
import withCSRF from '../../../../../commonComponents/withCSRF';
import { formatDate } from '../../../../../services/dateUtilityFunctions';
import FormModal from '../../../../../commonComponents/modal/FormModal';
import PdfFormModal from '../../../../../commonComponents/modal/PdfFormModal';
import { useModal } from '../../../../../services/customHooks';
import UserProp from '../../../../../commonComponents/propTypes/userProp';
import { PROVIDER_TYPE } from '../../../../../commonComponents/propTypes/marketProductForm';

const getUserForms = async userId => {
  try {
    const { data: response } = await axios.get(
      `/api/user/form/list?owner=${userId}`,
      {
        headers: {
          'X-Gamification-API': 'Games',
          'Accept-Version': '1.0.0',
        },
        params: {
          isPublic: true,
          populate: ['user'],
        },
      },
    );
    return response.data || [];
  } catch (err) {
    Notifications.showErrorFromRequest(err, 'ERROR_USER_FORMS');
    return [];
  }
};

const answerForm = async (
  t,
  _csrf,
  userId,
  userFormAnswerId,
  answer,
  userForm,
  link = null,
) => {
  try {
    const method = userFormAnswerId ? 'put' : 'post';
    const endpoint = userFormAnswerId
      ? `/api/user/form/answer/${userFormAnswerId}`
      : '/api/user/form/answer';

    const params = {
      userForm: userForm._id,
      providerType: userForm.providerType,
      owner: userId,
      formAnswerData: answer,
    };

    if (userForm.providerType === PROVIDER_TYPE.PDF) {
      params.formAnswersLink = link;
    }

    const { data: response } = await axios[method](endpoint, params, {
      headers: {
        'X-Gamification-API': 'Games',
        'Accept-Version': '1.0.0',
        'X-CSRF-Token': _csrf,
        _csrf,
      },
    });
    Notifications.showNotificationSuccess(
      t('success'),
      t('profile.user.forms.answered'),
    );
    return response.data || {};
  } catch (err) {
    Notifications.showErrorFromRequest(err, 'ERROR_USER_FORMS');
    return [];
  }
};

function UserForms({ t, _csrf, user, onRefetch }) {
  const [forms, setForms] = useState(undefined);
  const [formModal, setFormModal] = useModal({
    show: false,
    data: null,
  });
  const [answer, setAnswer] = useState({});

  const handleFormSave = async (userFormId, answers, link = null) => {
    if (answers) {
      const result = await answerForm(
        t,
        _csrf,
        user._id,
        formModal?.data?.answer?._id,
        answers,
        formModal?.data,
        link,
      );
      setAnswer(result);

      onRefetch?.();
    }

    setFormModal({
      show: false,
      data: null,
    });
  };

  useEffect(() => {
    getUserForms(user?._id).then(result => setForms(result));
  }, [answer]);

  return (
    <>
      {formModal.show ? (
        <>
          {formModal?.data?.providerType === PROVIDER_TYPE.SURVEY_JS ? (
            <FormModal
              form={{
                ...formModal.data,
                formQuestionData: formModal?.data?.providerData || {},
                formAnswerData: formModal?.data?.answer?.formAnswerData || {},
                productId: formModal?.data?._id,
              }}
              onClose={(pId, answers) => handleFormSave(pId, answers)}
            />
          ) : (
            <PdfFormModal
              form={{
                ...formModal.data,
                productForm: {
                  templatePDF: formModal?.data?.templatePDF || [],
                },
                formAnswersLink:
                  formModal?.data?.answer?.formAnswersLink || null,
              }}
              onClose={(pId, answers, link) =>
                handleFormSave(pId, answers, link)
              }
              opportunity={{
                status: formModal?.data?.answer?.formAnswersLink
                  ? 'completed'
                  : 'pending',
              }}
              userToFillForm={user}
              isEditable={!formModal?.data?.answer?.formAnswersLink}
            />
          )}
        </>
      ) : null}

      <div className="profile-user-forms">
        {(forms || [])?.length > 0 ? (
          forms.map(form => (
            <div key={form._id} className="profile-user-forms-item">
              <h3>{form.name}</h3>
              <p>{formatDate(form.createdAt)}</p>
              <button
                onClick={() => setFormModal({ show: true, data: form })}
                type="button"
                className={form?.answer ? 'btn btn-success' : 'btn btn-metal'}
              >
                {form?.answer
                  ? t('profile.forms.answer.check')
                  : t('profile.forms.answer')}
              </button>
            </div>
          ))
        ) : (
          <div className="no-forms-found">
            <span className="text">
              {forms === undefined
                ? `${t('loading')}...`
                : t('profile.no.user.forms')}
            </span>
          </div>
        )}
      </div>
    </>
  );
}

UserForms.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  user: PropTypes.shape(UserProp.propType).isRequired,
  onRefetch: PropTypes.func.isRequired,
};

export default compose(translate, withCSRF)(UserForms);
